// Wire codes of the binder share API (mtg-api app/binder_share.py,
// app/routes/binder_share.py, plan 2026-10-26 "Share a binder", Phase 1).
// Every literal here is a machine code the server sends or accepts, a path
// segment or a query key, never text a user reads; the copy lives in the
// `collection` catalog under binders.share.* and shared_binder.*.

/** Path segments. */
export const SHARE_SEG = {
  /** After /binders/{binder_id}: POST mints (or reuses), DELETE revokes. */
  share: '/share',
  /** The public read, outside the collection: /shared-binders/{token}. */
  sharedBinders: '/shared-binders/',
  /** After /shared-binders/{token}: the signed-in viewer's matches. */
  matches: '/matches',
} as const

/** Query keys of GET /shared-binders/{token}. */
export const SHARE_QUERY = { cursor: 'cursor', limit: 'limit', sort: 'sort', dir: 'dir', q: 'q' } as const

/** The public sorts (PUBLIC_SORTS), and each one's default direction (DEFAULT_DIR). */
export const PUBLIC_SORTS = ['name', 'set', 'value'] as const
export type PublicSort = typeof PUBLIC_SORTS[number]
export const SORT_DIRS = ['asc', 'desc'] as const
export type SortDir = typeof SORT_DIRS[number]
export const DEFAULT_DIR: Record<PublicSort, SortDir> = { name: 'asc', set: 'asc', value: 'desc' }
export const DEFAULT_SORT: PublicSort = 'name'

/** The page size the public page asks for (the server allows 1..200, default 120). */
export const SHARED_PAGE_LIMIT = 120

/** A share token as the server accepts it (binder_share.TOKEN_RE). */
export const SHARE_TOKEN_RE = /^[A-Za-z0-9_-]{8,32}$/

/** `detail` codes of the share routes. */
export const SHARE_DETAIL = {
  /** Unknown, revoked, binder deleted, or the owner's gate off: one identical 404. */
  shareNotFound: 'share_not_found',
  /** A stale or foreign cursor: restart from the top. */
  badCursor: 'bad_cursor',
  /** More than 20,000 aggregated public rows (the mint refuses it too). */
  binderTooLarge: 'binder_too_large',
  /** A deck box (or Unsorted) cannot be shared. */
  binderNotShareable: 'binder_not_shareable',
  /** The owner's decks could not be read for a spare binder (retryable). */
  decksUnavailable: 'decks_unavailable',
  /** This server has not migrated the binders tables yet. */
  bindersNotReady: 'binders_not_ready',
} as const

/** The public read's failure kinds (the page's states). */
export const SHARED_ERR = {
  notFound: 'not_found',
  badCursor: 'bad_cursor',
  tooLarge: 'too_large',
  decksUnavailable: 'decks_unavailable',
  /** 503 binders_not_ready: the server has not migrated yet (retryable). */
  notReady: 'not_ready',
  unavailable: 'unavailable',
  unauthorized: 'unauthorized',
  rateLimited: 'rate_limited',
  decode: 'decode',
  server: 'server',
  network: 'network',
} as const
export type SharedErrKind = typeof SHARED_ERR[keyof typeof SHARED_ERR]

/** React Query roots (never under the owner's collection keys). */
export const SHARE_QK = {
  sharedBinder: 'shared-binder',
  sharedBinderMatches: 'shared-binder-matches',
} as const

/** The public page's layouts. */
export const SHARED_LAYOUTS = ['grid', 'list'] as const
export type SharedLayout = typeof SHARED_LAYOUTS[number]

/** The App Analytics campaign of the public page's get-the-app ask. */
export const SHARED_BINDER_CAMPAIGN = 'shared_binder'

/** The QR's download file name and the share sheet's brand title. */
export const SHARE_QR_FILE = 'commanderbracket-binder-qr.png'
export const SHARE_QR_CANVAS_ID = 'binder-share-qr-canvas'
export const BRAND = 'CommanderBracket'

/** The `finish` code that gets the FOIL tag. */
export const FOIL_FINISH = 'foil'
